import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

export const ICONS = {
  truck: "M2 5h12v13H2z M14 10h4l4 5v3h-8 M5 18a2 2 0 1 0 .01 0 M17 18a2 2 0 1 0 .01 0",
  search: "M16 10a6 6 0 1 1-12 0 6 6 0 0 1 12 0 M15 15l6 6",
  clock: "M21 13a9 9 0 1 1-18 0 9 9 0 0 1 18 0 M12 7v6l4 3 M10 1h4",
  pin: "M19 9c0 6-7 12-7 12S5 15 5 9a7 7 0 1 1 14 0 M14 9a2 2 0 1 1-4 0 2 2 0 0 1 4 0",
  chat: "M5 4h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H7l-5 3V7a3 3 0 0 1 3-3 M8 9h7 M8 13h3",
  plus: "M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0 M12 8v8 M8 12h8",
  chevron: "m7 10 5 5 5-5",
  close: "m7 7 10 10 M7 17 17 7",
  calendar: "M4 5h16v16H4z M4 10h16 M8 2v6 M16 2v6",
  box: "m12 3 9 5-9 5-9-5z M3 8v10l9 5 9-5V8 M12 13v10 M8 5l9 5",
  trash: "M3 6h18 M9 6V3h6v3 M6 6l1 15h10l1-15 M10 10v7 M14 10v7",
  home: "M3 10 12 3l9 7 M5 9v12h5v-7h4v7h5V9",
  menu: "M4 6h16 M4 12h16 M4 18h16",
  doc: "M5 5h14v17H5z M5 10h14 M9 5V3h6v7 M12 10v4",
  money: "M3 5h18v15H3z M3 10h18 M6 15h4",
  bell: "M5 17h14l-2-4V9a5 5 0 0 0-10 0v4z M10 21h4",
  user: "M15 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0 M5 21v-3a7 7 0 0 1 14 0v3",
};

export const Ic = ({ n, d, ...rest }) => (
  <svg aria-hidden="true" className="icon" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" viewBox="0 0 24 24" {...rest}>
    <path d={d || ICONS[n]} />
  </svg>
);

/** Vehicle thumbnail: real tractor photo when the backend has one, else a neutral truck glyph. */
export function VehiclePhoto({ v, className = "vehicle-photo" }) {
  if (v?.photo) {
    return <img alt={v.mark} className={className} src={v.photo} style={{ objectFit: "cover", borderRadius: 6, background: "#f1f5fa" }} />;
  }
  return (
    <svg aria-label={v?.mark || "Машина"} className={className} role="img" viewBox="0 0 48 41">
      <rect fill="#eef4fb" height="41" rx="7" width="48" />
      <g fill="none" stroke="#7d93bb" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.7" transform="translate(7.5 7) scale(1.3)">
        <path d={ICONS.truck} />
      </g>
    </svg>
  );
}

export function StatusPill({ state, meta, as: Tag = "span", ...rest }) {
  const m = meta[state] || meta.idle;
  return (
    <Tag className={`status ${m.cls}`} {...rest}>
      <i />
      <span>{state === "tomorrow" ? <>Освобождается<br />завтра</> : m.label}</span>
    </Tag>
  );
}

export function MobileNav() {
  const navigate = useNavigate();
  const items = [
    ["Главная", "/", "home", false],
    ["Поиск", "/search", "search", false],
    ["Создать", "/order", "plus", false],
    ["Чаты", "/chats", "chat", false],
    ["Меню", "/settings", "menu", false],
  ];
  return (
    <nav aria-label="Мобильное меню" className="mobile-nav">
      {items.map(([label, to, ic]) => (
        <button key={to} onClick={() => navigate(to)}>
          <Ic n={ic} />
          {label}
        </button>
      ))}
    </nav>
  );
}

/** Wireframe toast (#toast.visible). */
export function useToast() {
  const [text, setText] = useState("");
  const [visible, setVisible] = useState(false);
  const timer = useRef(null);
  const toast = useCallback((t) => {
    clearTimeout(timer.current);
    setText(t);
    setVisible(true);
    timer.current = setTimeout(() => setVisible(false), 4200);
  }, []);
  useEffect(() => () => clearTimeout(timer.current), []);
  const node = (
    <div aria-live="polite" className={visible ? "visible" : ""} id="toast" role="status">
      {text}
    </div>
  );
  return [toast, node];
}

/** Wireframe dialog (.modal > .dialog). */
export function Dialog({ title, onClose, children }) {
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="modal" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <section aria-labelledby="dialog-title" aria-modal="true" className="dialog" role="dialog">
        <header>
          <h2 id="dialog-title">{title}</h2>
          <button aria-label="Закрыть" className="icon-button" onClick={onClose} type="button">
            <Ic n="close" />
          </button>
        </header>
        <div>{children}</div>
      </section>
    </div>
  );
}

const BARS = [9, 14, 20, 28, 37, 44];
export function Metric({ idx, icon, value, label, small }) {
  return (
    <article className={`fleet-metric m${idx}`}>
      <span className="metric-icon"><Ic d={icon} /></span>
      <b>{value}</b>
      <span>{label}</span>
      {small ? <small>{small}</small> : null}
      <div aria-hidden="true" className="bars">
        {BARS.map((h) => <i key={h} style={{ height: `${h}px` }} />)}
      </div>
    </article>
  );
}

export const METRIC_ICONS = {
  total: ICONS.truck,
  free: ICONS.search,
  tomorrow: ICONS.clock,
  trip: ICONS.pin,
  requests: ICONS.chat,
};

/** Shown to roles without an own fleet (DISPATCHER / CLIENT): matching /truck/* would answer 403. */
export function NoFleetAccess() {
  const navigate = useNavigate();
  return (
    <main id="main">
      <section className="intro page-heading">
        <h1>Мой транспорт</h1>
        <p>
          Раздел доступен владельцам транспорта: водителям и транспортным компаниям. Для вашей роли (диспетчер) собственного
          автопарка нет — найти машины можно в разделе «Найти транспорт», а отклики на ваши грузы смотрите в «Мои грузы».
        </p>
      </section>
      <div style={{ marginTop: 24, display: "flex", gap: 10 }}>
        <button className="button primary" onClick={() => navigate("/search")} type="button">Найти транспорт</button>
        <button className="button" onClick={() => navigate("/my-orders")} type="button">Мои грузы</button>
      </div>
    </main>
  );
}
